/**
 * Shared dashboard period filter - 6 standardized presets (7 Days / 30 Days /
 * Current Month / Current FY / Last 12 Months / Custom), used identically on
 * Main, Sales, Purchase and Customer Analytics dashboards so the time window
 * a page is showing is never ambiguous.
 */
import React, { useEffect, useState } from 'react'
import { FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import {
  DATE_RANGE_PRESETS,
  DEFAULT_DATE_RANGE_PRESET,
  DateRangePresetKey,
  computePresetRange,
} from '../../utils/dateRangePresets'

interface PeriodSelectorProps {
  onChange: (dateFrom: string, dateTo: string, presetKey: DateRangePresetKey) => void
  defaultPreset?: DateRangePresetKey
  size?: 'small' | 'medium'
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  onChange,
  defaultPreset = DEFAULT_DATE_RANGE_PRESET,
  size = 'small',
}) => {
  const [preset, setPreset] = useState<DateRangePresetKey>(defaultPreset)
  const [customFrom, setCustomFrom] = useState<Date | null>(null)
  const [customTo, setCustomTo] = useState<Date | null>(null)

  // Resolve and report the initial preset on mount, and whenever a non-custom
  // preset is selected.
  useEffect(() => {
    if (preset === 'custom') return
    const { dateFrom, dateTo } = computePresetRange(preset)
    onChange(dateFrom, dateTo, preset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset])

  // Custom range only reports once both dates are picked.
  useEffect(() => {
    if (preset !== 'custom' || !customFrom || !customTo) return
    onChange(format(customFrom, 'yyyy-MM-dd'), format(customTo, 'yyyy-MM-dd'), 'custom')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <FormControl size={size} sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={preset}
            label="Period"
            onChange={(e) => setPreset(e.target.value as DateRangePresetKey)}
          >
            {DATE_RANGE_PRESETS.map((p) => (
              <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {preset === 'custom' && (
          <>
            <DatePicker
              label="From"
              value={customFrom}
              onChange={setCustomFrom}
              slotProps={{ textField: { size, sx: { width: 160 } } }}
              maxDate={customTo || undefined}
            />
            <DatePicker
              label="To"
              value={customTo}
              onChange={setCustomTo}
              slotProps={{ textField: { size, sx: { width: 160 } } }}
              minDate={customFrom || undefined}
              maxDate={new Date()}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default PeriodSelector
